import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'
import { I18nContext } from 'i18n'
import { FaTimesCircle, FaChevronDown } from 'react-icons/fa'
import { Badge, Form, Row, Col, Container } from 'react-bootstrap'
import {
  Input,
  StandardSelect,
  OrganizationPicker,
  MultiRangeSlider,
  ScholarshipPicker,
  InfoTooltipLink,
} from 'components'
import moment from 'moment'
import {
  isEmpty,
  omit,
  sortedUniq,
  compact,
  findIndex,
  chain,
  orderBy,
} from 'lodash'
import { useSchool } from 'hooks/useSchool'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import {
  filterDates,
  eligibilityCriteria,
  applicationType,
  YesNoStates,
} from 'services/options-lists'
import {
  DATE_DATA_FORMAT,
  KNOWN_COUNTRIES,
  MIN_GPA,
  MAX_GPA,
  MIN_CLASS_RANK,
  MAX_CLASS_RANK,
} from 'utils/constants'
import omitEmpty from 'omit-empty'
import nspIcon from 'images/nspa-icon.png'
import UnforeseenEvents from './UnforeseenEvents'
import Dates from './Dates'
import CitizenshipStatus from './CitizenshipStatus'
import GradeDegree from './GradeDegree'
import { BsArrowRightCircle } from 'react-icons/bs'
import  Dropdown  from '../Dropdown'

const SearchPanel = ({
  criteria,
  onFilter,
  onClear,
  defaultValue,
  enumSchemas: {
    eligibilityCriteria: criteriaSchema,
    organization: {
      organizationType: organizationTypeSchema,
      organizationCategory: organizationCategorySchema,
    },
    unforeseenEventCriteria: unforeseenEventsSchema,
    citizenshipEligibility: citizenshipSchema,
  },
  countryList,
  stateList,
}) => {
  const { translate } = useContext(I18nContext)
  const { dataSchools, setParams, params } = useSchool()
  const [filters, setFilters] = useState([])
  const [dateFrom, setDateFrom] = useState()
  const [dateTo, setDateTo] = useState()
  const [dateField, setDateField] = useState()
  const [keyWord, setKeyWord] = useState()
  const [colleges, setColleges] = useState([])
  const [schools, setSchools] = useState([])
  const [collegePage, setCollegePage] = useState(1)
  const [schoolPage, setSchoolPage] = useState(1)
  const [collegeQuery, setCollegeQuery] = useState()
  const [selects, setSelects] = useState({})
  const [checks, setChecks] = useState({})
  const [multiples, setMultiples] = useState({})
  const [selectedColleges, setSelectedColleges] = useState([])
  const [selectedSchools, setSelectedSchools] = useState([])
  const [loadingCollege, setLoadingCollege] = useState(false)
  const [loadingSchool, setLoadingSchool] = useState(false)
  const collegeType = useRef(null)
  const schoolType = useRef(null)
  const [demographicsSections, setDemographicsSections] = useState()
  const [counties, setCounties] = useState()
  const isNspaOptions = YesNoStates.map((yesNo) => ({
    ...yesNo,
    value: yesNo.value.toString(),
  }))

  useEffect(() => {
    const result = chain(criteriaSchema)
      .filter(({ cat }) => cat && cat !== 'Ancestry' && cat !== 'Other')
      .map(({ cat, elementValue: value }) => ({
        label: value,
        value: `${cat},${value}`,
        cat,
      }))
      .groupBy((x) => x.cat)
      .map((value, key) => ({
        options: value,
        ...(key !== 'Race' && { header: <hr className="border-secondary" /> }),
      }))
      .value()

    setDemographicsSections(result)
  }, [criteriaSchema])

  const stateOptions = useMemo(
    () => [
      {
        options: stateList.slice(
          0,
          findIndex(stateList, (o) => o.countryCode === 'CA')
        ),
      },
      {
        options: stateList.slice(
          findIndex(stateList, (o) => o.countryCode === 'CA')
        ),
        header: <hr className="border-secondary my-1" />,
      },
    ],
    [stateList]
  )

  useEffect(() => {
    if (defaultValue) {
      const defaultFilters = Object.keys(
        omitEmpty(
          omit(defaultValue, [
            'paginated',
            'page',
            'pageSize',
            'to',
            'from',
            'inGoApply',
          ])
        )
      ).map(
        (key) =>
          defaultValue[key] && key.replace('dateField', defaultValue?.dateField)
      )

      if (defaultValue?.dateField) {
        if (defaultValue?.from)
          setDateFrom(Date.parse(`${defaultValue?.from}T00:00:00`))
        if (defaultValue?.to)
          setDateTo(Date.parse(`${defaultValue?.to}T00:00:00`))
        if (defaultValue?.dateField)
          setDateField(findDefaultSelect(defaultValue?.dateField, filterDates))
      }

      //Ignore false empty or undefined properties
      setFilters(defaultFilters.filter((item) => item))
    }
  }, [])

  useEffect(() => {
    //Get the counties
    const counties = stateList.reduce(
      (pre, { counties, countryCode, isoCode: stateCode }) => {
        if (counties) {
          pre.push(
            ...counties.map(({ label: county }) => ({
              value: `${county}, ${stateCode}`,
              county,
              countryCode,
              stateCode,
            }))
          )
        }
        return pre
      },
      []
    )
    setCounties(orderBy(counties, ['county'], ['asc']))
  }, [stateList, setCounties])

  const findDefaultSelect = (key, options) =>
    options?.find(({ value }) => key === value) || null

  const handleFilter = useCallback(
    (filter, value) => {
      if (!filters.includes(filter) && value) {
        const newFilter = filters.concat(filter)
        setFilters(newFilter)
      }

      if (onFilter) onFilter({ [filter]: value }, true)
    },
    [filters, setFilters, onFilter]
  )

  const handleChange = (e) => {
    setDateField(e)

    if (!e) {
      setDateFrom(null)
      setDateTo(null)
    }

    const fil = { dateField: e?.value }
    if (dateFrom) {
      fil.from = moment(dateFrom).format(DATE_DATA_FORMAT)
    }

    if (dateTo) {
      fil.to = moment(dateTo).format(DATE_DATA_FORMAT)
    }

    if (dateFrom || dateTo) {
      onFilter(fil, true)
    }

    //Add to applied filters
    const checkout = filterDates.map(({ value }) => value)
    const newfilter = filters.filter((f) => !checkout.includes(f))

    const usedFilters = e?.value ? newfilter.concat(e.value) : newfilter
    setFilters(usedFilters)
  }

  useEffect(() => {
    if (dataSchools) {
      if (params.schoolType) {
        setColleges(colleges.concat(dataSchools.schools))
        setLoadingCollege(false)
      } else {
        setSchools(schools.concat(dataSchools.schools))
        setLoadingSchool(false)
      }
    }
  }, [dataSchools])

  const handleDateChange = (date, target) => {
    const fil = { dateField: dateField?.value }
    const infoDate = date ? moment(date).format(DATE_DATA_FORMAT) : ''
    if (target === 'from') {
      setDateFrom(date)
      fil.from = infoDate
    } else {
      setDateTo(date)
      fil.to = infoDate
    }

    onFilter(fil, true)

    //Add to applied filters
    const checkout = filterDates.map(({ value }) => value)
    const newfilter = filters.filter((f) => !checkout.includes(f))

    const usedFilters = dateField?.value
      ? newfilter.concat(dateField?.value)
      : newfilter
    setFilters(usedFilters)
  }

  const removeFilter = (filter) => {
    //Remove from the applied filter
    const newfilter = filters.filter((f) => f !== filter)
    setFilters(newfilter)
  }

  const handleSelectChange = (e, filter) => {
    handleFilter(filter, e?.value || e)
    const newSelect = {}
    newSelect[filter] = e
    setSelects({ ...selects, ...newSelect })

    const textFilters = ['globalSearch', 'programName', 'org']

    textFilters.forEach((fil) => {
      if (filter === fil && isEmpty(e)) {
        setFilters(filters.filter((f) => f !== fil))
      }
    })

    if (!e) {
      removeFilter(filter)
    }
  }

  const handleCheckChange = (filter, value) => {
    //Check if the filter is nationwide
    const isNationwide = filter === 'nationwidePrograms' && value
    if (isNationwide) {
      //Add the nationwide and country filters or the selected ones
      setFilters([...filters, filter, 'countries'])
      onFilter(
        {
          nationwidePrograms: true,
          //Add the selected user countries or add the nationwide countries
          countries: isEmpty(defaultValue?.countries)
            ? KNOWN_COUNTRIES
            : defaultValue?.countries,
          states: [],
          countyCodes: [],
          cities: [],
        },
        true
      )
    }
    !isNationwide && handleFilter(filter, value)
    const newCheck = {}
    newCheck[filter] = value
    setChecks({ ...checks, ...newCheck })
    if (!value) removeFilter(filter)
  }

  const handleMultiChange = (filter, value, optionValue) => {
    //Check if we need to remove he nationwide filter
    if (filter === 'countries' && !value) {
      removeNationWideCountries()
      return
    }
    //Fix the data
    const realFilter =
      filter === 'citizenshipEligibilities' ? 'citizenshipStatuses' : filter
    const data = value ? value.map((v) => v[optionValue || 'value']) : []
    handleFilter(realFilter, data)
    const newMultiple = {}
    newMultiple[realFilter] = value
    setMultiples({ ...multiples, ...newMultiple })
    if (isEmpty(data)) {
      removeFilter(realFilter)
    }
  }

  const handlePagination = (e) => {
    const page = collegePage + 1
    setCollegePage(page)
    setParams({ name: collegeQuery, page, schoolType: 'college' })
  }

  const handlePaginationSchool = (e) => {
    const page = schoolPage + 1
    setCollegePage(page)
    setParams({ name: collegeQuery, page })
  }

  const handleSearch = (query) => {
    setLoadingCollege(true)
    setCollegeQuery(query)
    setCollegePage(1)
    setParams({ name: query, page: collegePage, schoolType: 'college' })
  }

  const handleSearchSchool = (query) => {
    setLoadingSchool(true)
    setCollegeQuery(query)
    setSchoolPage(1)
    setParams({ name: query, page: schoolPage })
  }

  const handleRemoveClick = (filter) => {
    removeFilter(filter)

    switch (filter) {
      case 'openDate':
      case 'closeDate':
      case 'lastVerifiedOn':
        setDateFrom(null)
        setDateTo(null)
        handleChange()
        setDateField(null)
        break
      case 'contest':
      case 'meritBased':
      case 'needBased':
      case 'nationwidePrograms':
      // case 'extendedData':
        handleCheckChange(filter, false)
        break
      case 'globalSearch':
      case 'org':
      case 'eligibilityCriteria':
      case 'applicationType':
      case 'minimumGpaRange':
      case 'minimumClassRankPercentage':
      case 'programName':
      case 'isNspaMember':
      case 'openDateRange':
      case 'closeDateRange':
        handleSelectChange(null, filter)
        break
      case 'states':
      case 'countyCodes':
      case 'cities':
      case 'orgStates':
      case 'organizationTypes':
      case 'organizationCategories':
      case 'unforeseenEvents':
      case 'demographics':
        handleMultiChange(filter, [])
        break
      case 'countries':
        //Check if we need to remove also the nationwide filter
        if (!defaultValue?.nationwidePrograms) {
          handleMultiChange(filter, [])
        } else {
          removeNationWideCountries()
        }
        break
      case 'colleges':
        setSelectedColleges([])
        handleFilter('colleges', [])
        break
      case 'schools':
        setSelectedSchools([])
        handleFilter('schools', [])
        break
      case 'citizenshipRequirements':
      case 'seekingDegree':
      case 'gradeLevel':
        setMultiples({
          ...multiples,
          [filter]: null,
          [`${filter}List`]: null,
        })
        onFilter({
          [filter]: null,
          [`${filter}List`]: null,
        })
        const newFilters = filters?.filter(
          (f) => f !== filter && f !== `${filter}List`
        )
        setFilters(newFilters)
      default:
        break
    }
  }

  const handleClearAll = () => {
    setFilters([])
    setKeyWord('')
    setDateField(null)
    setDateFrom('')
    setDateTo('')
    setChecks({})
    setSelects({
      org: null,
      eligibilityCriteria: eligibilityCriteria[0],
      applicationType: applicationType[0],
      minimumGpaRange: null,
      minimumClassRankPercentage: null,
      programName: null,
      isNspaMember: null,
      globalSearch: null,
    })
    setMultiples({
      citizenshipEligibilities: null,
      graduationStatuses: null,
      conditions: null,
      currentGradeLevels: null,
      degreeSoughts: null,
      states: null,
      countries: null,
      countyCodes: null,
      cities: null,
      organizationTypes: null,
      organizationCategories: null,
      orgStates: null,
      unforeseenEvents: null,
      demographics: null,
      citizenshipRequirements: null,
      citizenshipRequirementsList: null,
      seekingDegree: null,
      seekingDegreeList: null,
      gradeLevelList: null,
      gradeLevel: null,
      globalSearch: null,
    })

    setSelectedColleges([])
    setSelectedSchools([])

    onClear()
  }

  const handleTypeaheadChange = ([{ name }]) => {
    const newData = selectedColleges.concat(name)
    setSelectedColleges(newData)
    collegeType.current.clear()

    handleFilter('colleges', newData)
  }

  const handleRemoveCollege = (ix, source) => {
    if (source === 'colleges') {
      const newData = selectedColleges.filter((_, i) => i !== ix)
      setSelectedColleges(newData)

      handleFilter('colleges', newData)
      if (isEmpty(newData)) removeFilter('colleges')
    } else {
      const newData = selectedSchools.filter((_, i) => i !== ix)
      setSelectedSchools(newData)

      handleFilter('schools', newData)
      if (isEmpty(newData)) removeFilter('schools')
    }
  }

  const handleTypeaheadSchoolChange = ([{ name }]) => {
    const newData = selectedSchools.concat(name)
    setSelectedSchools(newData)
    schoolType.current.clear()

    handleFilter('schools', newData)
  }

  const renderCollegePills = (selected, source) => (
    <CollegeColumn>
      {selected?.map((name, ix) => (
        <Chip key={ix}>
          {name} <Close onClick={() => handleRemoveCollege(ix, source)} />
        </Chip>
      ))}
    </CollegeColumn>
  )

  const buildDefaultMultiples = (section) => {
    return (
      defaultValue?.[section]?.map((item) => ({
        value: item,
        label: item,
      })) || multiples?.[section]
    )
  }

  //Removes the selected countries and uncheck the nationwide checkbox
  const removeNationWideCountries = () => {
    //Remove the filter pills
    setTimeout(() => {
      setFilters(
        filters.filter((f) => f !== 'countries' && f !== 'nationwidePrograms')
      )
    })

    //Send the request without nationwide and countries
    onFilter(
      {
        nationwidePrograms: false,
        countries: [],
      },
      true
    )

    //Uncheck the nationwide checkbox
    setChecks({ ...checks, nationwidePrograms: false })
  }

  const getStateValue = useCallback(
    (key) => {
      return (
        defaultValue?.[key]?.map((item) => ({
          isoCode: item,
          name: stateList?.find(({ isoCode }) => item === isoCode)?.name,
        })) || multiples?.[key]
      )
    },
    [defaultValue, multiples, stateList]
  )

  //Remove filter if the values are equal to default
  useEffect(() => {
    if (
      defaultValue?.minimumGpaRange &&
      MIN_GPA === defaultValue?.minimumGpaRange[0] &&
      MAX_GPA === defaultValue?.minimumGpaRange[1]
    ) {
      handleRemoveClick('minimumGpaRange')
    }

    if (
      defaultValue?.minimumClassRankPercentage &&
      MIN_CLASS_RANK === defaultValue?.minimumClassRankPercentage[0] &&
      MAX_CLASS_RANK === defaultValue?.minimumClassRankPercentage[1]
    ) {
      handleRemoveClick('minimumClassRankPercentage')
    }
  }, [defaultValue])

  const formatGroupLabel = useCallback(({ header }) => header || null, [])
  const styles = useMemo(
    () => ({
      menu: (provided, state) => ({
        ...provided,
        ...(state.placement === 'bottom' && { marginBottom: '2rem' }),
        zIndex: 9999,
      }),
      group: () => ({ paddingBottom: 0, paddingTop: 0 }),
    }),
    []
  )

  const renderAppliedFilters = useCallback(
    (pills) => {
      const hiddenFilters = ['sort', 'sortDirection', 'member']
      const appliedFilters = chain(filters)
        .map((f) => f.replace('List', ''))
        .filter((f) => hiddenFilters.indexOf(f) < 0)
        .uniq()
        .value()

      return (
        <>
          {pills && (
            <Row>
              <Col>
                {appliedFilters.map((f, ix) => (
                  <Chip key={ix}>
                    {translate(f)}{' '}
                    <Close onClick={() => handleRemoveClick(f)} />
                  </Chip>
                ))}
              </Col>
            </Row>
          )}

          <Row>
            <ButtonContainer>
              <Button
                disabled={isEmpty(appliedFilters)}
                onClick={handleClearAll}
              >
                {translate('search_clear')}
              </Button>
            </ButtonContainer>
          </Row>
        </>
      )
    },
    [filters]
  )

  return (
    <PanelContainer>
      <Container fluid>
        <Row>
          <Col>{translate('search_applied')}</Col>
        </Row>
        {renderAppliedFilters(true)}
        <Divider />
        <Row className="mb-3">
          <Col>
            <SearchContainer>
              <Dropdown
                searchOnEnter={true}
                source={[]}
                labelKey="value"
                emptyLabel=""
                renderMenu={() => null}
                onChange={(e) => {
                  // handleFilter('globalSearch', e)
                  handleSelectChange(e, 'globalSearch')
                }}
                multiple
                allowNew
                placeholder="Search by Keyword"
                selected={selects?.globalSearch || compact(defaultValue?.globalSearch)}
                showRightIcon
              />
              {keyWord && (
                <Close onClick={() => handleRemoveClick('globalSearch')} />
              )}
            </SearchContainer>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <SearchContainer>
              <ScholarshipPicker
                onChange={(e) => {
                  // handleFilter('programName', e)
                  handleSelectChange(e, 'programName')
                }}
                flip={false}
                searchKey="programName"
                searchOnEnter
                showRightIcon
                selected={selects?.programName || compact(defaultValue?.programName)}
                placeholder="Search by Program Name"
                multiple
                allowNew
              />
            </SearchContainer>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <OrganizationPicker
              onChange={(e) => {
                handleSelectChange(e, 'org')
                // handleFilter('org', e)
                // handleSelectChange
              }}
              flip={false}
              searchKey="org"
              searchOnEnter
              showRightIcon
              selected={selects?.org || compact(defaultValue?.org)}
              placeholder="Search by Organization"
              multiple
              allowNew
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              options={organizationTypeSchema}
              onChange={(types) =>
                handleMultiChange('organizationTypes', types, 'elementValue')
              }
              value={
                defaultValue?.organizationTypes?.map((item) => ({
                  elementValue: item,
                })) || multiples?.organizationTypes
              }
              isClearable
              getOptionLabel={({ elementValue }) => elementValue}
              getOptionValue={({ elementValue }) => elementValue}
              placeholder="Organization Type"
              isSearchable={false}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              options={organizationCategorySchema}
              onChange={(types) =>
                handleMultiChange(
                  'organizationCategories',
                  types,
                  'elementValue'
                )
              }
              value={
                defaultValue?.organizationCategories?.map((item) => ({
                  elementValue: item,
                })) || multiples?.organizationCategories
              }
              isClearable
              getOptionLabel={({ elementValue }) => elementValue}
              getOptionValue={({ elementValue }) => elementValue}
              placeholder="Organization Category"
              isSearchable
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              placeholder="Organization State"
              options={stateOptions}
              onChange={(e) => handleMultiChange('orgStates', e, 'isoCode')}
              value={getStateValue('orgStates')}
              getOptionLabel={({ name }) => name}
              getOptionValue={({ isoCode }) => isoCode}
              {...{ formatGroupLabel }}
            />
          </Col>
        </Row>
        <Dates
          {...{
            translate,
            handleSelectChange,
            defaultValue,
            selects,
          }}
        />
        <Row className="mt-4">
          <Col>
            <div className="d-flex">
              <img className="w-10 pb-1" src={nspIcon} alt="NSPA Icon" />
              NSPA Member
              <InfoTooltipLink
                text={translate('tooltipProgramSearchNSPAMember')}
                link={translate('programSearchNSPAMemberLink')}
                className="ml-2"
              />
            </div>
            <StandardSelect
              options={isNspaOptions}
              onChange={(e) => handleSelectChange(e, 'isNspaMember')}
              value={findDefaultSelect(
                defaultValue?.isNspaMember || selects['isNspaMember'],
                isNspaOptions
              )}
              isClearable
              isSearchable={false}
              placeholder=""
            />
          </Col>
        </Row>
        {/* Temporarily hidden: Include Extended Data checkbox
        <Row>
          <Col className="d-flex">
            <Form.Check
              type="checkbox"
              label="Include Extended Data"
              onChange={(e) =>
                handleCheckChange('extendedData', e.target.checked)
              }
              checked={defaultValue?.extendedData || !!checks?.extendedData}
            />
            <InfoTooltipLink
              link={translate('programSearchExtendedDataLink')}
              text={translate('tooltipProgramSearchExtendedData')}
              className="ml-2"
              placement="top"
            />
          </Col>
        </Row>
        */}
        <Divider />
        <Row>
          <Col>
            <EligibilityTitle>{translate('search_eligibility')}</EligibilityTitle>
          </Col>
        </Row>
        <Row>
          <Col>
            <StyledTitle className="d-flex">
              {translate('search_location')}{' '}
              <InfoTooltipLink
                text={translate('search_location_info')}
                className="ml-2"
              />
            </StyledTitle>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Check
              label={translate('nationwidePrograms')}
              onChange={(e) =>
                handleCheckChange('nationwidePrograms', e.target.checked)
              }
              checked={
                defaultValue?.nationwidePrograms || !!checks?.nationwidePrograms
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>{translate('countries')}</Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              placeholder=""
              getOptionLabel={({ name }) => name}
              getOptionValue={({ iso2 }) => iso2}
              options={countryList}
              onChange={(e) => handleMultiChange('countries', e, 'iso2')}
              value={
                defaultValue?.countries?.map((item) => ({
                  iso2: item,
                  name: item,
                })) || multiples?.countries
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>{translate('states')}</Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              placeholder=""
              options={stateOptions}
              onChange={(e) => handleMultiChange('states', e, 'isoCode')}
              value={getStateValue('states')}
              isDisabled={!!defaultValue?.nationwidePrograms}
              getOptionLabel={({ name }) => name}
              getOptionValue={({ isoCode }) => isoCode}
              {...{ formatGroupLabel }}
            />
          </Col>
        </Row>
        <Row>
          <Col>{translate('counties')}</Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              placeholder=""
              options={counties}
              getOptionLabel={({ value }) => value}
              getOptionValue={({ value }) => value}
              onChange={(e) => handleMultiChange('countyCodes', e, 'value')}
              value={buildDefaultMultiples('countyCodes')}
              isDisabled={!!defaultValue?.nationwidePrograms}
            />
          </Col>
        </Row>
        <Row>
          <Col>{translate('cities')}</Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              placeholder=""
              options={sortedUniq(criteria?.locationCities).map((item) => ({
                value: item,
                label: item,
              }))}
              onChange={(e) => handleMultiChange('cities', e)}
              value={buildDefaultMultiples('cities')}
              isDisabled={!!defaultValue?.nationwidePrograms}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Check
              type="checkbox"
              label="Contest"
              onChange={(e) => handleCheckChange('contest', e.target.checked)}
              checked={defaultValue?.contest || !!checks?.contest}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Check
              type="checkbox"
              label="Merit Based"
              onChange={(e) =>
                handleCheckChange('meritBased', e.target.checked)
              }
              checked={defaultValue?.meritBased || !!checks?.meritBased}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Check
              type="checkbox"
              label="Need Based"
              onChange={(e) => handleCheckChange('needBased', e.target.checked)}
              checked={defaultValue?.needBased || !!checks?.needBased}
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            Demographics
            <InfoTooltipLink
              link={translate('programSearchDemographicsLink')}
              text={translate('tooltipProgramSearchDemographics')}
              className="ml-2"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              isMulti
              options={demographicsSections}
              onChange={(types) => handleMultiChange('demographics', types)}
              value={
                defaultValue?.demographics?.map((item) => ({
                  label: item.split(',')[1],
                  value: item,
                })) || multiples?.demographics
              }
              isClearable
              placeholder=""
              isSearchable
              {...{
                formatGroupLabel,
                styles,
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col className="d-flex">
            Minimum GPA
            <InfoTooltipLink
              text={translate('tooltipProgramSearchMinimumGPA')}
              className="ml-2"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <MultiRangeSlider
              min={MIN_GPA}
              max={MAX_GPA}
              onChange={(e) => handleSelectChange(e, 'minimumGpaRange')}
              step={0.1}
              value={defaultValue?.minimumGpaRange}
              format="0.0"
            />
          </Col>
        </Row>
        <Row>
          <Col>Minimum Class Rank Percentage</Col>
        </Row>
        <Row>
          <Col>
            <MultiRangeSlider
              min={MIN_CLASS_RANK}
              max={MAX_CLASS_RANK}
              onChange={(e) =>
                handleSelectChange(e, 'minimumClassRankPercentage')
              }
              step={10}
              value={defaultValue?.minimumClassRankPercentage}
              format="0%"
            />
          </Col>
        </Row>
        <CitizenshipStatus
          {...{
            translate,
            filters,
            criteria,
            multiples,
            defaultValue,
            onFilter,
            setMultiples,
            setFilters,
            citizenshipSchema,
          }}
        />
        <GradeDegree
          {...{
            translate,
            defaultValue,
            criteria,
            multiples,
            setMultiples,
            onFilter,
            filters,
            setFilters,
          }}
        />
        <Row>
          <Col>{translate('search_attendance')}</Col>
        </Row>
        <Row>
          <Col>
            <Typeahead
              id="collegeType"
              isLoading={loadingCollege}
              options={colleges}
              labelKey="name"
              minLength={4}
              maxResults={25 - 1}
              onPaginate={handlePagination}
              onSearch={handleSearch}
              paginate
              onChange={handleTypeaheadChange}
              placeholder={translate('search_college')}
              ref={collegeType}
              renderMenuItemChildren={({ id, name, schoolAddress }) => (
                <CollegeContent key={id}>
                  <p>{name}</p>
                  <p>{`${schoolAddress?.city}, ${schoolAddress?.stateOrProvince}`}</p>
                </CollegeContent>
              )}
              useCache={false}
            />
          </Col>
        </Row>
        <Row>
          {renderCollegePills(
            defaultValue?.colleges || selectedColleges,
            'colleges'
          )}
        </Row>
        <Row>
          <Col>{translate('search_current')}</Col>
        </Row>
        <Row>
          <Col>
            <Typeahead
              isLoading={loadingSchool}
              id="schoolType"
              options={schools}
              labelKey="name"
              minLength={4}
              maxResults={25 - 1}
              onPaginate={handlePaginationSchool}
              onSearch={handleSearchSchool}
              paginate
              onChange={handleTypeaheadSchoolChange}
              ref={schoolType}
              placeholder={translate('search_school')}
              renderMenuItemChildren={({
                id,
                name,
                schoolAddress,
                schoolType,
              }) => (
                <CollegeContent key={id}>
                  <p>{name}</p>
                  <p>
                    <span>{schoolType}</span>
                    {`- ${schoolAddress?.city}, ${schoolAddress?.stateOrProvince}`}
                  </p>
                </CollegeContent>
              )}
              useCache={false}
            />
          </Col>
        </Row>
        <Row>
          {renderCollegePills(
            defaultValue?.schools || selectedSchools,
            'schools'
          )}
        </Row>
        <UnforeseenEvents
          {...{
            defaultValue,
            translate,
            unforeseenEventsSchema,
            multiples,
            filters,
            setFilters,
            handleMultiChange,
          }}
        />
        <Row>
          <Col>{translate('applicationType')}</Col>
        </Row>
        <Row>
          <Col>
            <StandardSelect
              options={applicationType}
              isSearchable={true}
              isClearable={true}
              onChange={(e) => handleSelectChange(e, 'applicationType')}
              value={findDefaultSelect(
                defaultValue?.applicationType || selects['applicationType'],
                applicationType
              )}
              placeholder=""
            />
          </Col>
        </Row>
        <Divider />
        {renderAppliedFilters(false)}
      </Container>
    </PanelContainer>
  )
}

const CollegeContent = styled.div`
  font-size: 0.8rem;

  > p {
    margin: unset;
    font-weight: 600;

    &:last-child {
      color: ${({ theme }) => theme.palette.grayscale[3]};
      font-weight: 300;

      > span {
        background-color: ${({ theme }) => theme.palette.primary[1]};
        padding: 0.2rem;
        color: ${({ theme }) => theme.palette.grayscale[8]};
        border-radius: 0.3rem;
        margin-right: 0.5rem;
      }
    }
  }
`

const ButtonContainer = styled(Col)`
  text-align: center;
  margin-top: 1rem;
`

const Button = styled.button`
  background-color: ${({ theme }) => theme.palette.primary[2]};
  border: 0;
  color: ${({ theme }) => theme.palette.grayscale[8]};
  font-size: 0.8rem;
  padding: 0.5rem 2rem;
  border-radius: 0.2rem;

  &:disabled {
    background-color: ${({ theme }) => theme.palette.grayscale[5]};
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Typeahead = styled(AsyncTypeahead)`
  margin-bottom: 1rem;
  input {
    border-radius: unset;

    &::placeholder {
      color: ${({ theme }) => theme.palette.grayscale[4]};
      font-size: 0.8rem;
    }
  }

  a {
    padding: 0.5rem;
  }
`

const Divider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale[5]};
  margin: 0.5rem 0;
`

const ChevronDown = styled(FaChevronDown)`
  color: ${({ theme }) => theme.palette.grayscale[2]};
  font-size: 1rem;
`

const SearchContainer = styled.div`
  position: relative;

  > svg {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  }
`

const StyledInput = styled(Input)`
  height: 2rem;
  font-size: 0.8rem;
`

const StyledTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.grayscale[1]};
  margin-top: 1rem;
  margin-bottom: 0.3rem;
`

const EligibilityTitle = styled(StyledTitle)`
  font-size: 1.1rem;
`

const PanelContainer = styled.div`
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.palette.grayscale[1]};

  > div {
    padding: 0;
  }
`

const Chip = styled(Badge)`
  border: 1px solid ${({ theme }) => theme.palette.grayscale[3]};
  color: ${({ theme }) => theme.palette.grayscale[1]};
  margin-top: 0.5rem;
  margin-right: 0.5rem;
  padding: 0.5rem;
`

const Close = styled(FaTimesCircle)`
  color: ${({ theme }) => theme.palette.grayscale[3]};
  cursor: pointer;
`

const CollegeColumn = styled(Col)`
  > span:last-child {
    margin-bottom: 1rem;
  }
`

export default SearchPanel
